import { Component, OnInit, OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ModalController, NavParams } from '@ionic/angular';

import { Ticket } from '@app/modules/model/ticket';
import { PaymentTerminal } from '@app/modules/model/payment-terminal';
import { deviceService, WSConnection } from '@app/modules/sockets';

import { languageSupport, languageService } from '@app/modules/common/language';
import { viewService } from '@app/modules/view';
import { paytefDirectService, PaytefResult } from '@app/components/model/ticket/ui-paytef/paytef-direct';

@Component({
    selector: 'ui-modal-paytefpay',
    templateUrl:'./ui-paytef.html',
    styleUrls: [
        './ui-paytef.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiPaytefPayModal extends languageSupport implements OnInit, OnDestroy {
    public _tickets: Array<Ticket> = [];
    public _terminal: PaymentTerminal = null;
    public _place: any = null;
    public _mode: 'sale' | 'refund' = 'sale';
    public _refundAmount: number = null;   // when set in refund mode, overrides the full ticket amount
    public _saleAmount: number = null;    // mixed payment: card leg amount only

    private _connection: WSConnection = null;
    private _status: string = 'connecting';
    private _cardStatus: string = '';
    private _txStatus: string = '';
    private _errorMessage: string = '';
    private _result: any = null;
    private _subscription: any = null;
    private _autoAcceptTimer: any = null;
    // Set when the modal is being dismissed while _startPayment* / WS
    // callbacks may still resolve. Guards every post-async state mutation
    // (markForCheck after view destroy throws in Ivy).
    private _destroyed: boolean = false;

    get IsRefund(){
        return this._mode == 'refund';
    }

    get AmountToPay(){
        // refund mode honours an explicit override (partial refund); sale
        // mode uses saleAmount when set (mixed card leg), else full total.
        if (this.IsRefund && this._refundAmount != null){
            return Math.round(this._refundAmount * 100) / 100;
        }
        if (!this.IsRefund && this._saleAmount != null){
            return Math.round(this._saleAmount * 100) / 100;
        }
        let _totalprice = 0;
        for(let _ticket of this._tickets){
            _totalprice += _ticket.price;
        }
        _totalprice = Math.round(_totalprice * 100) / 100;
        return _totalprice;
    }

    get AmountCents(){
        return Math.round(this.AmountToPay * 100);
    }

    get Status(){
        return this._status;
    }

    get StatusText(){
        switch(this._status){
            case 'connecting': return this.lang.tr('@paytef_status_connecting');
            case 'started': return this.lang.tr(this.IsRefund ? '@paytef_status_refunding' : '@paytef_status_waiting');
            case 'progress':
                if (this._cardStatus == 'waitingForCard') return this.lang.tr('@paytef_status_waiting_card');
                if (this._cardStatus == 'processing' || this._txStatus == 'processing') return this.lang.tr(this.IsRefund ? '@paytef_status_refunding' : '@paytef_status_processing');
                if (this._txStatus == 'waitingForUser') return this.lang.tr('@paytef_status_waiting_card');
                return this.lang.tr(this.IsRefund ? '@paytef_status_refunding' : '@paytef_status_processing');
            case 'approved': return this.lang.tr(this.IsRefund ? '@paytef_status_refunded' : '@paytef_status_approved');
            case 'denied': return this.lang.tr('@paytef_status_denied');
            case 'error': return this._errorMessage || this.lang.tr('@paytef_status_error');
            default: return '';
        }
    }

    get IsProcessing(){
        return this._status == 'connecting' || this._status == 'started' || this._status == 'progress';
    }

    get IsFinished(){
        return this._status == 'approved' || this._status == 'denied' || this._status == 'error';
    }

    get IsApproved(){
        return this._status == 'approved';
    }

    Price(price){
        let _parts = Math.abs(price).toString().split('.');
        let _int = _parts[0];
        let _dec = (_parts.length > 1) ? (_parts[1] + '0').slice(0, 2) : "00";
        return _int + ',' + _dec;
    }

    /************************/
    /* STARTS HERE          */
    /************************/

    constructor(
        private lang: languageService,
        private change: ChangeDetectorRef,
        private modalCtrl: ModalController,
        private params: NavParams,
        private devsvc: deviceService,
        private paytefDirect: paytefDirectService,
        public view: viewService
    ) {
        super(lang, change);
    }

    ngOnInit() {
        this._tickets = this.params.get('tickets');
        this._terminal = this.params.get('terminal');
        this._place = this.params.get('place');

        // optional — callers that open the modal for a refund pass
        // mode='refund' and may set refundAmount for partial refunds.
        let _mode = this.params.get('mode');
        if (_mode == 'refund'){
            this._mode = 'refund';
        }
        let _refundAmount = this.params.get('refundAmount');
        if (typeof _refundAmount === 'number' && _refundAmount > 0){
            this._refundAmount = _refundAmount;
        }
        let _saleAmount = this.params.get('saleAmount');
        if (typeof _saleAmount === 'number' && _saleAmount > 0){
            this._saleAmount = _saleAmount;
        }

        this._startPayment();
    }

    ngOnDestroy(){
        this._destroyed = true;
        super.OnDestroy();
        this._clearAutoAcceptTimer();
        this._cleanupConnection();
    }

    private _clearAutoAcceptTimer(){
        if (this._autoAcceptTimer){
            clearTimeout(this._autoAcceptTimer);
            this._autoAcceptTimer = null;
        }
    }

    private _scheduleAutoAccept(){
        // Payment already approved — no reason to force the cashier to
        // tap a button. Close the modal automatically after a short
        // delay so the check-mark is still visible and the flow feels
        // continuous. The timer is cancelled in ngOnDestroy (e.g. if
        // the modal gets dismissed for any other reason in the meantime).
        this._clearAutoAcceptTimer();
        this._autoAcceptTimer = setTimeout(() => {
            this._autoAcceptTimer = null;
            this.OnAccept();
        }, 1500);   // matches the PR description; 500ms was too short for the cashier to register the check-mark
    }

    // Stash terminal en cada ticket para que DoCommit emita el receipt PD+QR.
    // Solo en datafono con onpaycard. Refunds no auto-imprimen.
    private _triggerAutoprintIfNeeded(){
        if (this.IsRefund){
            return;
        }
        if (!this._terminal || !this._terminal.onpaycard){
            return;
        }
        if (!this.view.IsDatafono){
            return;
        }
        for (let _ticket of this._tickets){
            _ticket.RequestPaytefAutoPrint(this._terminal);
        }
    }

    /************************/
    /* PAYMENT FLOW         */
    /************************/

    private _cleanupConnection(){
        if (this._subscription){
            this._subscription.unsubscribe();
            this._subscription = null;
        }

        if (this._connection){
            this._connection.Disconnect();
            this._connection = null;
        }
    }

    private async _startPayment(){
        try {
            this._cleanupConnection();

            this._status = 'connecting';
            this.change.markForCheck();

            // Datáfono mode short-circuits the WS envelope and hits the
            // paytef HTTP API on 127.0.0.1:8887 directly — the app is
            // running on the same device as paytef so there's no remote
            // hop to worry about.
            if (this.view.IsDatafono){
                await this._startPaymentDirect();
                return;
            }

            // TPV mode: connect via WS. The terminal carries its own
            // RASPPI (the print service that physically owns the pinpad)
            // — connecting to place.till instead would talk to the bar's
            // service and fail SALE/REFUND when the cashier picks a
            // terminal from another POS. Fall back to place.till only
            // if the terminal has no rasppi configured (legacy data).
            let _till = (this._terminal && this._terminal.rasppi) || this._place.till;
            if (!_till){
                this._status = 'error';
                this._errorMessage = this.lang.tr('@paytef_error_no_service');
                this.change.markForCheck();
                return;
            }

            // Try local connection first
            this._connection = await this.devsvc.Connect(
                _till.wshost, _till.wsport, _till.name, true
            );

            // If local fails, try remote
            if (!this._connection){
                this._connection = await this.devsvc.Connect(
                    null, null, _till.name, false
                );
            }

            if (!this._connection){
                this._status = 'error';
                this._errorMessage = this.lang.tr('@paytef_error_no_connection');
                this.change.markForCheck();
                return;
            }

            // Cancelado DURANTE la conexión: si el cajero pulsó Cancelar mientras
            // conectábamos (estado 'connecting', _connection aún null -> OnCancel no
            // mandó CANCEL y cerró el modal), _startPayment sigue aquí tras el await
            // Connect. NO arrancar la venta -> evita un SALE (cobro) iniciado DESPUÉS
            // de cancelar. Cerramos la conexión recién abierta y salimos.
            if (this._destroyed){
                if (this._connection){
                    this._connection.Disconnect();
                    this._connection = null;
                }
                return;
            }

            // Subscribe to WS messages for progress updates
            this._subscription = this._connection.OnMessageRecv.subscribe(
            (msg) => {
                if (msg && msg['data']){
                    let _data = msg['data'];
                    if (typeof _data === 'string'){
                        try { _data = JSON.parse(_data); } catch(e) {}
                    }

                    if (_data && typeof _data['approved'] === 'boolean'){
                        this._handleTerminalResponse(_data);
                    }
                    else if (_data && _data['status']){
                        this._handleProgress(_data);
                    }
                }
            });

            // Send SALE or REFUND command. Reference is the ticket(s) orderno
            // joined — the Rust side indexes the sale cache by this value so
            // a later refund can resolve paytefOperationNumber for a
            // referenced refund without us storing it client-side.
            let _reference = this._tickets.map(t => t.orderno).join(', ');
            let _payload: any = {
                command: this.IsRefund ? 'REFUND' : 'SALE',
                host: this._terminal.host,
                port: 8887,
                amount: this.AmountCents,
                pinpad: '*',
                reference: _reference
            };
            let _action = JSON.stringify(_payload);

            this._status = 'started';
            this.change.markForCheck();

            let _response = await this._connection.Send('paytef', _action);

            // Modal may have been dismissed (manual fallback) while we
            // awaited the Send round-trip. Bail out instead of mutating
            // state on a detached view.
            if (this._destroyed){
                return;
            }

            if (_response && _response.exec == 'SUCCESS'){
                let _data = _response.data;
                if (typeof _data === 'string'){
                    try { _data = JSON.parse(_data); } catch(e) {}
                }

                this._handleTerminalResponse(_data);
            }
            else if (!this.IsFinished){
                this._status = 'error';
                let _data = _response?.data;
                if (typeof _data === 'string'){
                    try { _data = JSON.parse(_data); } catch(e) {}
                }
                this._errorMessage = (_data && _data.error) || this.lang.tr('@paytef_status_error');
            }

            this.change.markForCheck();

        } catch(e) {
            console.error('[PAYTEF] Payment error:', e);
            if (this._destroyed){
                return;
            }
            this._status = 'error';
            this._errorMessage = e.message || this.lang.tr('@paytef_status_error');
            this.change.markForCheck();
        }
    }

    private _handleTerminalResponse(_data: any){
        if (this._destroyed || this.IsFinished){
            return;
        }
        if (!_data || typeof _data.approved !== 'boolean'){
            return;
        }

        if (_data.approved){
            this._status = 'approved';
            this._result = _data;
            this._triggerAutoprintIfNeeded();
            this._scheduleAutoAccept();
        }
        else {
            this._status = 'denied';
            this._errorMessage = _data.resultText || this.lang.tr('@paytef_status_denied');
            this._result = _data;
        }
        this.change.markForCheck();
    }

    private _handleProgress(data){
        // Tras destroy o terminal, callbacks tardios no deben volver a
        // "procesando" (p.ej. WSL Send timeout 10s + progress suelto).
        if (this._destroyed || this.IsFinished){
            return;
        }
        if (data.status == 'progress' || data.status == 'started'){
            this._status = data.status;
            this._txStatus = data.transactionStatus || '';
            this._cardStatus = data.cardStatus || '';
            this.change.markForCheck();
        }
    }

    // Datafono: pega contra el API HTTP de paytef sin WS — los progress alimentan los mismos getters.
    private async _startPaymentDirect(){
        this._status = 'started';
        this.change.markForCheck();

        let _reference = this._tickets.map(t => t.orderno).join(', ');
        let _pinpad = '*';
        let _opts = {
            pinpad: _pinpad,
            reference: _reference,
            onProgress: (progress) => {
                this._handleProgress({
                    status: progress.status,
                    transactionStatus: progress.transactionStatus || '',
                    cardStatus: progress.cardStatus || ''
                });
            }
        };

        let _result: PaytefResult;
        try {
            if (this.IsRefund){
                _result = await this.paytefDirect.refund(this.AmountCents, _opts);
            }
            else {
                _result = await this.paytefDirect.sale(this.AmountCents, _opts);
            }
        }
        catch(e){
            console.error('[PAYTEF-DIRECT] Unexpected error:', e);
            if (this._destroyed){
                return;
            }
            this._status = 'error';
            this._errorMessage = this.lang.tr('@paytef_status_error');
            this.change.markForCheck();
            return;
        }

        // OnManualDuringProcessing / close may have dismissed the modal
        // during the long poll. Don't touch state on a destroyed view.
        if (this._destroyed){
            return;
        }

        if (_result.status == 'approved'){
            this._status = 'approved';
            this._result = _result;
            this._triggerAutoprintIfNeeded();
            this._scheduleAutoAccept();
        }
        else if (_result.status == 'denied'){
            this._status = 'denied';
            this._errorMessage = _result.resultText || this.lang.tr('@paytef_status_denied');
            this._result = _result;
        }
        else {
            // paytef-direct devuelve la key i18n (@paytef_error_*) — traducir aqui.
            this._status = 'error';
            this._errorMessage = _result.error
                ? this.lang.tr(_result.error)
                : this.lang.tr('@paytef_status_error');
        }
        this.change.markForCheck();
    }

    /************************/
    /* USER ACTIONS         */
    /************************/

    // Helper para mandar cancel fire-and-forget al terminal. Solo se
    // usa en OnClose y OnManual — los otros handlers (OnCancel,
    // OnManualDuringProcessing) ya enviaban cancel en su propia
    // logica, no se tocan en esta PR.
    //
    // El endpoint paytef /pinpad/cancel es idempotente: si no hay
    // operacion en curso responde exitosamente sin efecto, asi que
    // llamarlo aunque la SALE haya fallado/terminado es safe.
    private _sendCancelBestEffort(source: string){
        const _pinpad = '*';

        if (this.view.IsDatafono){
            const _ret: any = this.paytefDirect.cancel(_pinpad);
            if (_ret && typeof _ret.catch === 'function'){
                _ret.catch((e: any) => console.warn('[PAYTEF] ' + source + ' direct cancel failed:', e));
            }
        }
        else if (this._connection){
            const _action = JSON.stringify({
                command: 'CANCEL',
                host: this._terminal && this._terminal.host,
                port: 8887,
                pinpad: _pinpad
            });
            try {
                const _ret: any = this._connection.Send('paytef', _action);
                if (_ret && typeof _ret.catch === 'function'){
                    _ret.catch((e: any) => console.warn('[PAYTEF] ' + source + ' WS cancel failed:', e));
                }
            }
            catch(e){
                console.warn('[PAYTEF] ' + source + ' WS cancel send threw:', e);
            }
        }
    }

    async OnCancel(){
        // Marca de teardown YA: si _startPayment está esperando el await Connect
        // (estado 'connecting'), al reanudar verá _destroyed=true y NO arrancará la
        // venta -> evita un cobro iniciado DESPUÉS de cancelar (Copilot #1772).
        this._destroyed = true;

        if (this.IsProcessing){
            // Cancel al terminal con espera ACOTADA (~4s), no el await completo:
            // el Send de paytef tiene timeout de 3min, así que si el terminal NO
            // responde al CANCEL (justo cuando el cajero pulsa Cancelar porque el
            // datáfono se colgó) el modal se quedaba SIN cerrar hasta 3min -> "el
            // botón de cancelar no funciona". Pero tampoco fire-and-forget puro: el
            // modal CIERRA su _connection al destruirse (Disconnect), y cerrar el
            // socket antes de que el CANCEL se transmita haría que NO llegue a
            // paytef. Con la carrera contra 4s damos tiempo a que salga (y a la
            // respuesta si llega) y cerramos igual. El /pinpad/cancel es idempotente.
            const _cancelTimeout = new Promise(resolve => setTimeout(resolve, 4000));
            try {
                if (this.view.IsDatafono){
                    await Promise.race([
                        Promise.resolve(this.paytefDirect.cancel('*')).catch(() => {}),
                        _cancelTimeout
                    ]);
                }
                else if (this._connection){
                    let _action = JSON.stringify({
                        command: 'CANCEL',
                        host: this._terminal && this._terminal.host,
                        port: 8887,
                        pinpad: '*'
                    });
                    await Promise.race([
                        this._connection.Send('paytef', _action).catch(() => {}),
                        _cancelTimeout
                    ]);
                }
            }
            catch(e){
                console.warn('[PAYTEF] OnCancel cancel failed:', e);
            }
        }

        await this.modalCtrl.dismiss(false);
    }

    async OnAccept(){
        this._clearAutoAcceptTimer();
        await this.modalCtrl.dismiss({
            approved: true,
            result: this._result
        });
    }

    async OnRetry(){
        this._status = 'connecting';
        this._errorMessage = '';
        this._result = null;
        this.change.markForCheck();

        this._startPayment();
    }

    async OnManual(){
        // El cashier marca como cobrado externamente tras error/denied.
        // Sin cancel, el datafono se queda mostrando el importe esperando
        // tarjeta aunque el ticket ya este resuelto fuera.
        this._sendCancelBestEffort('OnManual');
        this._triggerAutoprintIfNeeded();
        await this.modalCtrl.dismiss({
            manual: true
        });
    }

    // Escape hatch durante el processing: cancel best-effort + dismiss como manual.
    async OnManualDuringProcessing(){
        let _pinpad = '*';

        // Fire-and-forget — no await para que el modal cierre rapido. .catch() en ambos paths.
        try {
            if (this.view.IsDatafono){
                let _ret: any = this.paytefDirect.cancel(_pinpad);
                if (_ret && typeof _ret.catch === 'function'){
                    _ret.catch((e: any) => console.warn('[PAYTEF] Direct cancel failed:', e));
                }
            }
            else if (this._connection){
                let _action = JSON.stringify({
                    command: 'CANCEL',
                    host: this._terminal.host,
                    port: 8887,
                    pinpad: _pinpad
                });
                let _ret: any = this._connection.Send('paytef', _action);
                if (_ret && typeof _ret.catch === 'function'){
                    _ret.catch((e: any) => console.warn('[PAYTEF] WS cancel send failed:', e));
                }
            }
        }
        catch(e){
            console.warn('[PAYTEF] Cancel during manual fallback failed:', e);
        }

        // Mismo hook que OnManual — datafono necesita el receipt en cualquier caso.
        this._triggerAutoprintIfNeeded();
        await this.modalCtrl.dismiss({
            manual: true
        });
    }

    async OnClose(){
        // Cerrar tras error/denied — el SALE pudo haber llegado al
        // terminal antes de la perdida de conexion, asi que el datafono
        // sigue mostrando el importe. Cancel best-effort para no dejar
        // el terminal colgado.
        this._sendCancelBestEffort('OnClose');
        await this.modalCtrl.dismiss(false);
    }
}
